
import { useMemo } from 'react';
import { canUpdateSunStatus, isOlderThan } from '@/lib/utils/timezoneUtils';
import { UpdateConditions } from './types';

interface UseUpdateConditionsProps {
  terraceId: string;
  lastChecked: string | undefined;
  isUpdating: boolean;
  autoUpdateThresholdMinutes: number;
  checkAutoRateLimit: () => { allowed: boolean; waitMinutes?: number };
}

export const useUpdateConditions = ({
  terraceId,
  lastChecked,
  isUpdating,
  autoUpdateThresholdMinutes,
  checkAutoRateLimit
}: UseUpdateConditionsProps): UpdateConditions => {
  return useMemo(() => {
    if (!terraceId || isUpdating) {
      return { shouldUpdate: false, reason: 'missing terraceId or already updating' };
    }

    // Check time window
    const timeCheck = canUpdateSunStatus('auto-update');
    if (!timeCheck.canUpdate) {
      return { shouldUpdate: false, reason: timeCheck.reason };
    }

    // Check rate limiting for auto updates
    const rateLimitCheck = checkAutoRateLimit();
    if (!rateLimitCheck.allowed) {
      return { shouldUpdate: false, reason: `rate limited, wait ${rateLimitCheck.waitMinutes} minutes` };
    }

    // Check if data is old enough to warrant update
    const isDataOld = isOlderThan(lastChecked, autoUpdateThresholdMinutes);
    if (!isDataOld) {
      const now = Date.now();
      const ageMinutes = lastChecked ? Math.floor((now - new Date(lastChecked).getTime()) / (1000 * 60)) : 'unknown';
      return { shouldUpdate: false, reason: `data is recent (${ageMinutes} minutes old)` };
    }
    
    return { shouldUpdate: true, reason: 'all conditions met' };
  }, [terraceId, lastChecked, isUpdating, autoUpdateThresholdMinutes]);
};
